import React from "react";
// eslint-disable-next-line import/no-cycle
import CustomEditor from "./CustomEditor";
import CustomTextField from "./CustomTextField";
import CustomSelect from "./CustomSelect";
import CustomSwitch from "./CustomSwitch";
import CustomAutocomplete from "./CustomAutocomplete";
import CustomAutocompleteAsync from "./CustomAutocompleteAsync";
import CustomCheckbox from "./CustomCheckbox";
import CustomDecimal from "./CustomDecimal";
import CustomMultiSelect from "./CustomMultiSelect";
import CustomSlider from "./CustomSlider";
import CustomIconPicker from "./CustomIconPicker";
import CustomDateRange from "./CustomDateRange";
import CustomDateTimePicker from "./CustomDateTimePicker";
import CustomDatePicker from "./CustomDatePicker";
import CustomTimePicker from "./CustomTimePicker";

export default function InputSwitch(props) {
    const {
        type,
        disabled,
        formDisabled,
        required,
        fullWidth,
        fullHeight,
        neededWidth, // only select, for TextField + Select inside flex line
        options, // only select, autocomplete
        getOptions, // autocomplete-async
        getSelectedOption, // autocomplete-async
        optionsKey, // only select, autocomplete(-async)
        optionsName, // only select, autocomplete(-async)
        optionsFormat, // only select, autocomplete(-async)
        labelFormat, // only autocomplete(-async)
        optionsValueType = "object", // only select, autocomplete(-async): "object" or "key"
        falseLabel, // only switch
        trueLabel, // only switch
        checked, // only switch
        align = "left", // only switch
        falseValueColor, // only switch
        trueValueColor, // only switch
        labelPlacement, // only checkbox
        label,
        placeholder,
        defaultValue,
        value,
        onChange,
        onBlur,
        onKeyDown,
        onInputChange, // autocomplete
        blurOnSelect, // autocomplete
        startAdornment,
        endAdornment,
        sx,
        className,
        multiline,
        rows,
        children,
        filterDisabled,
        filterData,
        filterOptions,
        addOption,
        disableClearable,
        allowShowPassword,
        onClick,
        isLoading,
        maxHeight,
        autoFocus,
        allowCopy,
        clearField,
        menuItemStyle,
        icon,
        checkedIcon,
        color,
        freeSolo,
        allowEmpty,
        dateParseTemplate,
        allowNegative, // decimal
        thousandSeparator, // decimal
        decimalSeparator, // decimal
        decimalScale, // decimal
        fixedDecimalScale, // decimal
        inputLink,
        onClear,
        maxValue, // slider
        minValue, // slider
        marks, // slider
        valueLabelDisplay, // slider
        valueLabelFormat, // slider
        step, // slider
        inputProps,
        maxDateTime, // datetimepicker
        minDateTime, // datetimepicker
        inputStyle,
        tabId, // for Boilerplates Dialog in CustomEditor
        disableEmptySelection,
        onContextMenu,
        forcedRootBlock,
    } = props;

    switch (type) {
    case "checkbox":
        return (
            <CustomCheckbox
                label={label}
                labelPlacement={labelPlacement || "end"}
                sx={sx}
                disabled={disabled || formDisabled}
                className={className}
                required={required}
                value={value}
                checked={value}
                onChange={onChange}
                isLoading={isLoading}
                icon={icon}
                checkedIcon={checkedIcon}
                color={color}
            />
        );
    case "editor":
        return (
            <CustomEditor
                label={label}
                disabled={disabled || formDisabled}
                required={required}
                value={value}
                className={className}
                onChange={onChange}
                fullWidth={fullWidth}
                fullHeight={fullHeight}
                isLoading={isLoading}
                tabId={tabId}
                sx={sx}
                forcedRootBlock={forcedRootBlock}
            />
        );
    case "icon":
        return (
            <CustomIconPicker
                label={label}
                disabled={disabled || formDisabled}
                required={required}
                value={value}
                className={className}
                onChange={onChange}
                isLoading={isLoading}
            />
        );
    case "autocomplete-async":
        return (
            <CustomAutocompleteAsync
                fullWidth={fullWidth}
                disabled={disabled || formDisabled}
                required={required}
                label={label}
                className={className}
                value={value}
                onChange={onChange}
                onInputChange={onInputChange}
                blurOnSelect={blurOnSelect}
                getOptions={getOptions}
                getSelectedOption={getSelectedOption}
                optionsKey={optionsKey}
                optionsValueType={optionsValueType}
                optionsName={optionsName}
                optionsFormat={optionsFormat}
                labelFormat={labelFormat}
                sx={sx}
                isLoading={isLoading}
                autoFocus={autoFocus}
                startAdornment={startAdornment}
                endAdornment={endAdornment}
                freeSolo={freeSolo}
                filterDisabled={filterDisabled}
                placeholder={placeholder}
                onClear={onClear}
                onKeyDown={onKeyDown}
            />
        );
    case "autocomplete":
        return (
            <CustomAutocomplete
                fullWidth={fullWidth}
                disabled={disabled || formDisabled}
                className={className}
                required={required}
                label={label}
                value={value}
                onChange={onChange}
                onInputChange={onInputChange}
                blurOnSelect={blurOnSelect}
                options={options}
                optionsKey={optionsKey}
                optionsValueType={optionsValueType}
                optionsName={optionsName}
                optionsFormat={optionsFormat}
                labelFormat={labelFormat}
                sx={sx}
                filterData={filterData}
                filterDisabled={filterDisabled}
                addOption={addOption}
                disableClearable={disableClearable}
                isLoading={isLoading}
                freeSolo={freeSolo}
            />
        );
    case "switch":
        return (
            <CustomSwitch
                fullWidth={fullWidth}
                className={className}
                disabled={disabled || formDisabled}
                label={label}
                defaultValue={defaultValue}
                checked={checked}
                onChange={onChange}
                falseLabel={falseLabel}
                trueLabel={trueLabel}
                align={align}
                sx={sx}
                isLoading={isLoading}
                icon={icon}
                checkedIcon={checkedIcon}
                trueColor={trueValueColor}
                falseColor={falseValueColor}
            />
        );
    case "select":
        return (
            <CustomSelect
                fullWidth={fullWidth}
                neededWidth={neededWidth}
                disabled={disabled || formDisabled}
                placeholder={placeholder}
                required={required}
                className={className}
                label={label}
                defaultValue={defaultValue}
                value={value}
                onChange={onChange}
                onBlur={onBlur}
                options={options}
                optionsKey={optionsKey}
                optionsValueType={optionsValueType}
                optionsName={optionsName}
                optionsFormat={optionsFormat}
                filterDisabled={filterDisabled}
                filterData={filterData}
                filterOptions={filterOptions}
                sx={sx}
                isLoading={isLoading}
                maxHeight={maxHeight}
                menuItemStyle={menuItemStyle}
            >
                {children}
            </CustomSelect>
        );

    case "multiselect":
        return (
            <CustomMultiSelect
                fullWidth={fullWidth}
                neededWidth={neededWidth}
                disabled={disabled || formDisabled}
                placeholder={placeholder}
                required={required}
                className={className}
                label={label}
                defaultValue={defaultValue}
                value={value}
                onChange={onChange}
                onBlur={onBlur}
                options={options}
                optionsKey={optionsKey}
                optionsValueType={optionsValueType}
                optionsName={optionsName}
                optionsFormat={optionsFormat}
                filterDisabled={filterDisabled}
                filterData={filterData}
                sx={sx}
                isLoading={isLoading}
                maxHeight={maxHeight}
                menuItemStyle={menuItemStyle}
                disableEmptySelection={disableEmptySelection}
            >
                {children}
            </CustomMultiSelect>
        );
    case "slider":
        return (
            <CustomSlider
                label={label}
                value={value || [0, 0]}
                onChange={(e) => onChange(e)}
                disabled={disabled}
                maxValue={maxValue}
                minValue={minValue}
                marks={marks}
                valueLabelDisplay={valueLabelDisplay}
                valueLabelFormat={valueLabelFormat}
                step={step}
            />
        );
    case "decimal":
        return (
            <CustomDecimal
                variant="outlined"
                fullWidth={fullWidth}
                disabled={disabled || formDisabled}
                className={className}
                required={required}
                label={label}
                placeholder={placeholder}
                type={type}
                value={value}
                onClick={onClick}
                onChange={onChange}
                onBlur={onBlur}
                startAdornment={startAdornment}
                endAdornment={endAdornment}
                allowShowPassword={allowShowPassword}
                sx={sx}
                multiline={multiline}
                rows={rows}
                isLoading={isLoading}
                autoFocus={autoFocus}
                allowCopy={allowCopy}
                clearField={clearField}
                allowEmpty={allowEmpty}
                allowNegative={allowNegative}
                thousandSeparator={thousandSeparator}
                decimalSeparator={decimalSeparator}
                decimalScale={decimalScale}
                fixedDecimalScale={fixedDecimalScale}
            />
        );
    case "daterange":
        return (
            <CustomDateRange
                label={label}
                onChange={onChange}
            />
        );
    case "datetimepicker":
        return (
            <CustomDateTimePicker
                fullWidth={fullWidth}
                label={label}
                required={required}
                disabled={disabled}
                onChange={onChange}
                maxDateTime={maxDateTime}
                minDateTime={minDateTime}
            />
        );
    case "timepicker":
        return (
            <CustomTimePicker
                {...props}
            />
        );
    case "datepicker":
        return (
            <CustomDatePicker
                {...props}
            />
        );
    default:
        return (
            <CustomTextField
                variant="outlined"
                fullWidth={fullWidth}
                disabled={disabled || formDisabled}
                className={className}
                required={required}
                label={label}
                defaultValue={defaultValue === false ? null : defaultValue}
                placeholder={placeholder}
                type={type}
                value={value}
                onClick={onClick}
                onChange={onChange}
                onBlur={onBlur}
                onKeyDown={onKeyDown}
                startAdornment={startAdornment}
                endAdornment={endAdornment}
                allowShowPassword={allowShowPassword}
                sx={sx}
                multiline={multiline}
                rows={rows}
                isLoading={isLoading}
                autoFocus={autoFocus}
                allowCopy={allowCopy}
                clearField={clearField}
                allowEmpty={allowEmpty}
                allowNegative={allowNegative}
                inputLink={inputLink}
                inputProps={inputProps}
                dateParseTemplate={dateParseTemplate}
                step={step}
                inputStyle={inputStyle}
                onContextMenu={onContextMenu}
            />
        );
    }
}
